/* eslint-disable no-useless-escape, no-dupe-keys */
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  LinearProgress
} from '@mui/material';
import Sidebar from './Sidebar';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LaunchIcon from '@mui/icons-material/Launch';
import { useAuth } from '../contexts/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import Tooltip from '@mui/material/Tooltip';
import { Link as RouterLink } from 'react-router-dom';
import { internalProblemPathFromLeetcodeUrl, isInternalRoute } from '../utils/problemLinks';

// Updated data structure for flashcards
const flashcardsByTopic = {
  'arrays-and-hashing': {
    title: 'Arrays & Hashing',
    problems: [
      {
        id: 1,
        name: 'Two Sum',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/two-sum',
        flashcards: [
          {
            question: 'What is the brute force approach for Two Sum and its time complexity?',
            answer: 'The brute force approach uses nested loops to check every pair of numbers. Time complexity is O(n²).'
          },
          {
            question: 'How can we optimize Two Sum using a Hash Map?',
            answer: 'Store each number in a hash map as we iterate. For each number, check if its complement (target - num) exists in the hash map. This gives us O(n) time complexity.'
          }
        ]
      },
      {
        id: 2,
        name: 'Group Anagrams',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/group-anagrams',
        flashcards: [
          {
            question: 'What is the key insight for solving Group Anagrams efficiently?',
            answer: 'Sort each string or use a character count as a key in a hash map. All anagrams will have the same sorted string/character count.'
          }
        ]
      }
    ]
  },
  'binary-search': {
    title: 'Binary Search',
    problems: [
      {
        id: 1,
        name: 'Binary Search Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/binary-search',
        flashcards: [
          {
            question: 'What is the key idea behind binary search?',
            answer: 'Dividing the search space in half at each step by comparing the target with the middle element.'
          },
          {
            question: 'What are the three common mistakes in binary search?',
            answer: '1. Integer overflow, 2. Off-by-one errors, 3. Infinite loops'
          },
          {
            question: 'Name three real-world applications of binary search.',
            answer: '1. Finding a word in a dictionary, 2. Finding a clip in a video, 3. Finding the first bad commit in version control'
          }
        ]
      },
      {
        id: 2,
        name: 'Binary Search Templates',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-first-and-last-position-of-element-in-sorted-array',
        flashcards: [
          {
            question: 'When do we use the Min template in binary search?',
            answer: 'When we want to find the first occurrence of a condition (first True in FFF...TTT pattern).'
          },
          {
            question: 'When do we use the Max template in binary search?',
            answer: 'When we want to find the last occurrence of a condition (last True in TTT...FFF pattern).'
          },
          {
            question: 'Why do we need different mid calculations for Min and Max templates?',
            answer: 'To prevent infinite loops. For Max template, we use ceiling division to ensure the range always decreases.'
          }
        ]
      },
      {
        id: 3,
        name: 'Find First and Last Position',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-first-and-last-position-of-element-in-sorted-array',
        flashcards: [
          {
            question: 'How do we find the first position of an element in a sorted array?',
            answer: 'Use the Min template with check(mid) = nums[mid] >= target'
          },
          {
            question: 'How do we find the last position of an element in a sorted array?',
            answer: 'Use the Max template with check(mid) = nums[mid] <= target'
          }
        ]
      },
      {
        id: 4,
        name: 'Search Insert Position',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/search-insert-position',
        flashcards: [
          {
            question: 'What is the key difference between Search Insert Position and regular Binary Search?',
            answer: 'We don\'t need to check if the target exists at the end - the returned index is always the correct insert position.'
          },
          {
            question: 'Why do we initialize right = len(nums) instead of len(nums)-1 in Search Insert Position?',
            answer: 'Because the target might need to be inserted at the end of the array.'
          }
        ]
      },
      {
        id: 5,
        name: 'Find Peak Element',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-peak-element',
        flashcards: [
          {
            question: 'What is a peak element?',
            answer: 'An element that is strictly greater than its neighbors. For edge elements, we consider nums[-1] = nums[n] = -∞.'
          },
          {
            question: 'How do we determine which half to search in Find Peak Element?',
            answer: 'If nums[mid] < nums[mid+1], search right half. Otherwise, search left half.'
          },
          {
            question: 'Why can we use binary search to find a peak element?',
            answer: 'Because if nums[mid] < nums[mid+1], there must be a peak in the right half. Otherwise, there must be a peak in the left half.'
          }
        ]
      }
    ]
  },
  'backtracking': {
    title: 'Backtracking',
    problems: [
      {
        id: 1,
        name: 'Backtracking Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/backtracking/',
        flashcards: [
          { question: 'What is backtracking used for in algorithms?', answer: 'Backtracking is used to enumerate all possible solutions by exploring every choice at each step, often for problems with exponential solution spaces.' },
          { question: 'What is the key recursive idea behind backtracking?', answer: 'Build a solution of size n by extending a solution of size n-1, trying all possible choices at each step.' },
          { question: 'Why do we use curr.copy() when appending to the result in backtracking?', answer: 'To ensure each result is a separate list; otherwise, all results would reference the same list object.' },
          { question: 'What is the time complexity of generating all subsets of a set of size n?', answer: 'O(n*2^n), since there are 2^n subsets and each can have up to n elements.' }
        ]
      },
      {
        id: 2,
        name: 'Subsets & Duplicates',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/subsets-ii/',
        flashcards: [
          { question: 'How do you avoid duplicate subsets when the input contains duplicates?', answer: 'Sort each subset and use a set to store unique subsets.' },
          { question: 'What is the difference between combinations and permutations in backtracking?', answer: 'Combinations do not consider order, while permutations do.' },
          { question: 'What is the key insight for generating all subsets (the power set) using backtracking?', answer: 'At each step, either include or exclude the current element, leading to 2^n possible subsets.' }
        ]
      },
      {
        id: 3,
        name: 'Permutations',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/permutations/',
        flashcards: [
          { question: 'What is the key insight for generating all permutations using backtracking?', answer: 'Swap the current element with each possible choice and recurse on the next index to generate all possible orderings.' },
          { question: 'What is the general template for a backtracking function?', answer: 'Recursively try all choices at each step, backtrack (undo) after each choice, and add valid results to the answer list.' }
        ]
      },
      {
        id: 4,
        name: 'Combination Sum',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/combination-sum/',
        flashcards: [
          { question: 'How does the backtracking template change for Combination Sum problems?', answer: 'You allow repeated use of the same element by recursing on the same index, and prune when the current sum exceeds the target.' },
          { question: 'What is the key insight for solving Combination Sum with backtracking?', answer: 'Allow repeated use of the same element by not incrementing the index in recursion; prune when the current sum exceeds the target.' }
        ]
      },
      {
        id: 5,
        name: 'Pruning & Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/n-queens/',
        flashcards: [
          { question: 'What is pruning in backtracking?', answer: 'Pruning is the process of stopping recursion early when it is impossible for the current path to lead to a valid solution.' },
          { question: 'Give an example of a classic backtracking problem.', answer: 'Generating all valid parentheses combinations, N-Queens, subsets, permutations, and combination sum are classic backtracking problems.' }
        ]
      },
      {
        id: 6,
        name: 'Palindrome Partitioning',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/palindrome-partitioning/',
        flashcards: [
          { question: 'What is the key insight for Palindrome Partitioning using backtracking?', answer: 'At each step, try all substrings starting at the current index and recurse only if the substring is a palindrome.' }
        ]
      }
    ]
  },
  'prefix-sum': {
    title: 'Prefix Sums',
    problems: [
      {
        id: 1,
        name: 'Prefix Sums Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-immutable/',
        flashcards: [
          {
            question: 'What is a prefix sum and what is it used for?',
            answer: 'A prefix sum is an array where each element at index i is the sum of all elements from index 0 to i in the original array. It is used to answer range sum queries in O(1) time after O(n) preprocessing.'
          },
          {
            question: 'What is the formula to compute the sum of a subarray [l, r] using prefix sums?',
            answer: 'PrefixSum[r] - PrefixSum[l-1] (assuming PrefixSum[-1] = 0).'
          },
          {
            question: 'Why do we sometimes initialize prefix sums with a 0 at the start?',
            answer: 'So that PrefixSum[-1] = 0, making sum([0, x]) = PrefixSum[x] - PrefixSum[-1] = PrefixSum[x].'
          },
          {
            question: 'Can prefix sums be efficiently updated if the array changes frequently?',
            answer: 'No, prefix sums are not efficient for updates. Use segment trees for efficient updates.'
          },
          {
            question: 'What is the identity element for addition, multiplication, and XOR in prefix operations?',
            answer: 'Addition: 0, Multiplication: 1, XOR: 0.'
          },
          {
            question: 'Give two other common prefix operations besides sum.',
            answer: 'Prefix multiplication and prefix XOR.'
          },
          {
            question: 'What is the general requirement for an operation to be used in prefix queries?',
            answer: 'The operation must be associative and have an inverse.'
          }
        ]
      },
      {
        id: 2,
        name: 'Range Sum Query - Immutable',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-immutable/',
        flashcards: [
          {
            question: 'What is the time complexity of sumRange(left, right) after prefix sum preprocessing?',
            answer: 'O(1) per query after O(n) preprocessing.'
          },
          {
            question: 'What is the main limitation of prefix sums for range queries?',
            answer: 'They cannot handle updates efficiently.'
          },
          {
            question: 'What Python built-in can be used to compute prefix sums?',
            answer: 'itertools.accumulate.'
          },
          {
            question: 'Write the Python code for a NumArray class that supports sumRange queries using prefix sums.',
            answer: 'class NumArray:\n    def __init__(self, nums):\n        self.prefix_sums = [0]\n        for num in nums:\n            self.prefix_sums.append(self.prefix_sums[-1] + num)\n    def sumRange(self, left, right):\n        return self.prefix_sums[right+1] - self.prefix_sums[left]'
          }
        ]
      },
      {
        id: 3,
        name: '2D Prefix Sums',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-2d-immutable/',
        flashcards: [
          {
            question: 'What is the formula for the sum of a submatrix using 2D prefix sums?',
            answer: 'PS[ii][jj] - PS[ii][j-1] - PS[i-1][jj] + PS[i-1][j-1]'
          },
          {
            question: 'What is the time complexity to answer a sumRegion query after 2D prefix sum preprocessing?',
            answer: 'O(1) per query after O(mn) preprocessing.'
          },
          {
            question: 'Write the Python code for a NumMatrix class that supports sumRegion queries using 2D prefix sums.',
            answer: 'class NumMatrix:\n    def __init__(self, matrix):\n        m, n = len(matrix), len(matrix[0])\n        self.dp = [[0]*(n+1) for _ in range(m+1)]\n        for i in range(m):\n            for j in range(n):\n                self.dp[i+1][j+1] = matrix[i][j] + self.dp[i][j+1] + self.dp[i+1][j] - self.dp[i][j]\n    def sumRegion(self, row1, col1, row2, col2):\n        return self.dp[row2+1][col2+1] - self.dp[row2+1][col1] - self.dp[row1][col2+1] + self.dp[row1][col1]'
          }
        ]
      },
      {
        id: 4,
        name: 'Prefix Sums Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-size-subarray-sum/',
        flashcards: [
          {
            question: 'How can prefix sums and binary search be combined to solve Minimum Size Subarray Sum?',
            answer: 'Use prefix sums to get subarray sums and binary search (bisect_right) to find the leftmost index where the sum is at least the target.'
          },
          {
            question: 'Why do we use bisect_right instead of bisect_left in this context?',
            answer: 'To handle duplicate prefix sums correctly.'
          },
          {
            question: 'What is a real-world application of 2D prefix sums?',
            answer: 'Efficiently answering sum queries over submatrices in image processing, games, or spreadsheets.'
          }
        ]
      },
      {
        id: 5,
        name: 'Prefix Multiplication and XOR',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/product-of-array-except-self/',
        flashcards: [
          {
            question: 'How can prefix multiplication be used to solve Product of Array Except Self?',
            answer: 'Compute prefix and suffix products for each index, then answer[i] = prefix[i-1] * suffix[i+1].'
          },
          {
            question: 'What is the formula to recover the original array from a prefix XOR array?',
            answer: 'arr[i] = pref[i] ^ pref[i-1] (XOR is its own inverse).'
          },
          {
            question: 'Write the Python code to recover the original array from a prefix XOR array.',
            answer: 'def findArray(pref):\n    for i in range(len(pref)-1, 0, -1):\n        pref[i] = pref[i] ^ pref[i-1]\n    return pref'
          }
        ]
      },
      {
        id: 6,
        name: 'Prefix Sums in Simulation',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-time-to-visit-all-houses/',
        flashcards: [
          {
            question: 'How are prefix sums used in the Minimum Time to Visit All Houses problem?',
            answer: 'Prefix sums allow O(1) computation of total distance in either direction on a circle.'
          },
          {
            question: 'Why do we need both prefix sums for forward and backward directions?',
            answer: 'To efficiently compute the shortest path in either direction for circular paths.'
          }
        ]
      }
    ]
  },
  'linked-list': {
    title: 'Linked List',
    problems: [
      {
        id: 1,
        name: 'Linked List Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/linked-list/',
        flashcards: [
          { question: 'What is a linked list', answer: 'A linear data structure where each node contains data and a reference to the next node.' },
          { question: 'What is the main advantage of a linked list over an array', answer: 'Linked lists allow O(1) insertions and deletions when you have a reference to the node, unlike arrays which may require shifting elements.' },
          { question: 'What is the dummy node pattern and why is it useful', answer: 'A dummy node is a sentinel node placed at the start of the list to simplify edge cases, especially when modifying the head.' }
        ]
      },
      {
        id: 2,
        name: 'Fast and Slow Pointers',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/linked-list-cycle/',
        flashcards: [
          { question: 'How does the fast slow pointer technique work', answer: 'One pointer moves faster than the other; it is used to find the middle or kth last node, or to detect cycles.' },
          { question: 'How do you find the middle node of a linked list', answer: 'Use fast and slow pointers; when fast reaches the end, slow is at the middle.' }
        ]
      },
      {
        id: 3,
        name: 'Reversal and Deletion',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/reverse-linked-list/',
        flashcards: [
          { question: 'What is the iterative template for reversing a linked list', answer: 'Iterate through the list, changing each nodes next pointer to point to the previous node.' },
          { question: 'How do you remove the nth node from the end of a linked list', answer: 'Use a dummy node and two pointers spaced n plus 1 apart; when the fast pointer reaches the end, the slow pointer is before the node to remove.' }
        ]
      },
      {
        id: 4,
        name: 'Advanced',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/copy-list-with-random-pointer/',
        flashcards: [
          { question: 'How do you delete a node in a singly linked list when only given access to that node', answer: 'Copy the value from the next node and delete the next node instead.' }
        ]
      }
    ]
  },
  'sorted-list': {
    title: 'Sorted List',
    problems: [
      {
        id: 1,
        name: 'SortedList Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        flashcards: [
          { question: 'What is a SortedList?', answer: 'A list that is always kept sorted and supports O(log n) insertion and deletion.' },
          { question: 'Which Python library provides the SortedList?', answer: 'The sortedcontainers library.' },
          { question: 'What does sl[i] return for a SortedList sl?', answer: 'The ith value in the sorted order.' }
        ]
      },
      {
        id: 2,
        name: 'Bisect Methods',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        flashcards: [
          { question: 'What does bisect_left(sl, x) return?', answer: 'The first index in sl with value >= x.' },
          { question: 'How do you find the largest number less than x in a SortedList sl?', answer: 'Use bisect_left(sl, x) - 1.' },
          { question: 'How do you find the largest number less than or equal to x in a SortedList sl?', answer: 'Use bisect_right(sl, x) - 1.' },
          { question: 'How do you find the smallest number greater than or equal to x in a SortedList sl?', answer: 'Use bisect_left(sl, x).' },
          { question: 'How do you find the smallest number greater than x in a SortedList sl?', answer: 'Use bisect_right(sl, x).' }
        ]
      },
      {
        id: 3,
        name: 'Tuple Sorting',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        flashcards: [
          { question: 'How are tuples sorted in a SortedList?', answer: 'By the first element, then by the next as a tiebreaker (lexicographically).' }
        ]
      },
      {
        id: 4,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://grantjenks.com/docs/sortedcontainers/sortedlist.html',
        flashcards: [
          { question: 'Give a common use case for SortedList in algorithm problems.', answer: 'Maintaining a sliding window of sorted values, efficiently finding predecessors and successors, or finding the kth largest element.' }
        ]
      }
    ]
  },
  'interval': {
    title: 'Interval',
    problems: [
      {
        id: 1,
        name: 'Interval Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/interval/',
        flashcards: [
          { question: 'What is an interval?', answer: 'A pair of integers representing a range, such as [start, end].' },
          { question: 'How do you determine if two intervals [a,b] and [c,d] overlap?', answer: 'They overlap if max(a,c) <= min(b,d).' },
          { question: 'What is the key insight for solving most interval problems efficiently?', answer: 'Sort the intervals and use greedy algorithms.' }
        ]
      },
      {
        id: 2,
        name: 'Merging & Overlap',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/merge-intervals/',
        flashcards: [
          { question: 'How do you merge overlapping intervals?', answer: 'Sort by start, then merge if the current start is <= the last end.' },
          { question: 'How do you check if a person can attend all meetings given a list of intervals?', answer: 'Check if any intervals overlap.' },
          { question: 'What is the minimum number of meeting rooms required for a set of intervals?', answer: 'The maximum number of overlapping intervals at any point.' }
        ]
      },
      {
        id: 3,
        name: 'Line Sweep & Meeting Rooms',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/meeting-rooms-ii/',
        flashcards: [
          { question: 'What is the line sweep algorithm used for in interval problems?', answer: 'Counting overlapping intervals, finding the maximum number of concurrent intervals, and efficiently handling a mix of intervals and point queries.' },
          { question: 'How do you find the intersection of two lists of intervals?', answer: 'Use two pointers and add the overlap if max(a,c) <= min(b,d).' },
          { question: 'How do you remove covered intervals?', answer: 'Sort by start, then by decreasing end, and skip intervals covered by the previous.' }
        ]
      },
      {
        id: 4,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/interval/',
        flashcards: [
          { question: 'What is the key insight for the My Calendar series of problems?', answer: 'Use line sweep or binary search to efficiently track overlapping events.' },
          { question: 'What does the running sum represent in the line sweep algorithm?', answer: 'The number of active intervals at a given point.' },
          { question: 'What is a real-world application of interval problems?', answer: 'Meeting room scheduling, resource allocation, time series analysis, and more.' }
        ]
      }
    ]
  },
  'greedy': {
    title: 'Greedy',
    problems: [
      {
        id: 1,
        name: 'Greedy Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/greedy/',
        flashcards: [
          { question: 'What is the greedy algorithmic paradigm?', answer: 'Making locally optimal choices at each step, hoping to reach a global optimum.' },
          { question: 'Why can greedy algorithms sometimes fail?', answer: 'They do not consider future consequences of current choices and may miss the global optimum.' },
          { question: 'How can you prove a greedy algorithm is correct?', answer: 'By using an exchange argument or greedy stays ahead proof.' }
        ]
      },
      {
        id: 2,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/jump-game/',
        flashcards: [
          { question: 'What is the greedy approach for Jump Game I?', answer: 'Track the maximum reachable index as you iterate through the array.' },
          { question: 'What is the greedy approach for Jump Game II?', answer: 'Track the end of the current jump range and increment jumps when you reach it.' },
          { question: 'What is the greedy approach for Container With Most Water?', answer: 'Use two pointers and always move the pointer at the shorter line.' },
          { question: 'What is the optimal time complexity for Container With Most Water?', answer: 'O(n) using the two-pointer greedy approach.' }
        ]
      },
      {
        id: 3,
        name: 'Advanced Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/trapping-rain-water/',
        flashcards: [
          { question: 'What is the greedy approach for Trapping Rain Water (O(1) space)?', answer: 'Use two pointers and track max heights from both ends.' },
          { question: 'What is the greedy approach for maximizing profit in task assignment?', answer: 'Always assign the highest profit available task to each worker.' },
          { question: 'What is the greedy approach for Furthest Building You Can Reach?', answer: 'Use ladders for the largest jumps and bricks for smaller ones, possibly swapping as you go.' }
        ]
      },
      {
        id: 4,
        name: 'Proofs & Pitfalls',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/greedy/',
        flashcards: [
          { question: 'What is a common pitfall when using greedy algorithms?', answer: 'Assuming greedy always gives the optimal solution; always check if it applies.' }
        ]
      }
    ]
  },
  'divide-and-conquer': {
    title: 'Divide and Conquer',
    problems: [
      {
        id: 1,
        name: 'Divide and Conquer Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/divide-and-conquer/',
        flashcards: [
          { question: 'What is the main idea behind divide and conquer algorithms?', answer: 'Divide the problem into subproblems, solve them recursively, and combine the results.' },
          { question: 'What is the "join" step in divide and conquer?', answer: 'The step where you combine results from subproblems.' },
          { question: 'Which theorem is commonly used to analyze the time complexity of divide and conquer algorithms?', answer: 'The Master Theorem.' }
        ]
      },
      {
        id: 2,
        name: 'Merge Sort & Inversions',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/sort-an-array/',
        flashcards: [
          { question: 'What is the time complexity of merge sort?', answer: 'O(n log n).' },
          { question: 'How do you merge two sorted arrays?', answer: 'Compare the smallest elements and add the smaller one to the result.' },
          { question: 'How can you count inversions in an array efficiently?', answer: 'Use merge sort and count cross-inversions during the merge step.' },
          { question: 'What is a cross-inversion?', answer: 'An inversion between the left and right subarrays.' }
        ]
      },
      {
        id: 3,
        name: 'Quad Trees & Grids',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/construct-quad-tree/',
        flashcards: [
          { question: 'What is the base case for most divide and conquer algorithms?', answer: 'When the input size is 1.' },
          { question: 'How do you construct a quad tree?', answer: 'Recursively divide the grid into four quadrants and build subtrees.' },
          { question: 'What is the time complexity of constructing a quad tree for an n x n grid?', answer: 'O(n^2).' },
          { question: 'What is the key insight for filling a special grid in the divide and conquer problem?', answer: 'Recursively fill each quadrant and increment values by offsets.' }
        ]
      },
      {
        id: 4,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/divide-and-conquer/',
        flashcards: [
          { question: 'Why is divide and conquer a powerful technique in software engineering?', answer: 'It allows you to break down complex problems into manageable subproblems.' }
        ]
      }
    ]
  },
  'palindrome': {
    title: 'Palindrome',
    problems: [
      {
        id: 1,
        name: 'Palindrome Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/palindrome/',
        flashcards: [
          { question: 'What is a palindrome?', answer: 'A string that reads the same forward and backward.' },
          { question: 'What is the recursive structure of a palindrome?', answer: 's[0] == s[-1] and the substring s[1:-1] is a palindrome.' },
          { question: 'What is a common interview problem involving palindromes?', answer: 'Longest palindromic substring, valid palindrome with deletions, counting palindromic substrings.' }
        ]
      },
      {
        id: 2,
        name: 'Palindrome Checking',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/valid-palindrome/',
        flashcards: [
          { question: 'How do you check if a string is a palindrome?', answer: 'Compare characters from both ends moving toward the center.' },
          { question: 'How do you check if a substring can be rearranged into a palindrome with at most k replacements?', answer: 'Check if the number of odd frequency characters divided by 2 is <= k.' },
          { question: 'What is the time complexity of checking if a string is a palindrome?', answer: 'O(n), where n is the length of the string.' }
        ]
      },
      {
        id: 3,
        name: 'Partitioning & Substrings',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/palindrome-partitioning/',
        flashcards: [
          { question: 'What is the key insight for Palindrome Partitioning using backtracking?', answer: 'At each step, try all substrings starting at the current index and recurse only if the substring is a palindrome.' },
          { question: 'How do you count all palindromic substrings in a string?', answer: 'Expand around every center and count palindromic substrings.' }
        ]
      },
      {
        id: 4,
        name: 'Advanced/Code',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/different-palindromic-substrings/',
        flashcards: [
          { question: 'Write the Python code for checking if a string is a palindrome.', answer: 'def isPalindrome(s): return s == s[::-1]' }
        ]
      }
    ]
  },
  'parenthesis': {
    title: 'Parenthesis',
    problems: [
      {
        id: 1,
        name: 'Valid Parentheses',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/valid-parentheses/',
        flashcards: [
          {
            question: 'What is the main data structure used for balancing parentheses?',
            answer: 'A stack, because it is last-in-first-out and matches the order of opening and closing parentheses.'
          },
          {
            question: 'What are the two main cases for unbalanced parentheses?',
            answer: 'Case 1: Extra closing parenthesis (no matching open). Case 2: Extra opening parenthesis (not closed).'
          },
          {
            question: 'How can you optimize the solution if there is only one type of parenthesis?',
            answer: 'Use a counter instead of a stack.'
          },
          {
            question: 'What is the time and space complexity of the stack-based solution?',
            answer: 'O(n) time and O(n) space.'
          },
          {
            question: 'Write the Python code for the stack-based valid parentheses check.',
            answer: 'def isValid(s):\n    paren = {\')\': \'(\', \'}\': \'{\', \'\]\': \'\[\'}\n    stack = []\n    for c in s:\n        if c in paren:\n            if not stack or stack[-1] != paren[c]:\n                return False\n            stack.pop()\n        else:\n            stack.append(c)\n    return not stack'
          }
        ]
      },
      {
        id: 2,
        name: 'Generate Parentheses',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/generate-parentheses/',
        flashcards: [
          {
            question: 'What algorithmic technique is used to generate all valid combinations of n pairs of parentheses?',
            answer: 'Backtracking.'
          },
          {
            question: 'How do you ensure a generated parenthesis string is valid during backtracking?',
            answer: 'Ensure the number of closing parentheses never exceeds the number of opening at any point.'
          },
          {
            question: 'Write the Python code for generating all valid parentheses using backtracking.',
            answer: 'def generateParenthesis(n):\n    res = []\n    def backtrack(curr, open, close):\n        if len(curr) == 2*n:\n            res.append(curr)\n            return\n        if open < n:\n            backtrack(curr+\'\(\', open+1, close)\n        if close < open:\n            backtrack(curr+\'\)\', open, close+1)\n    backtrack(\'\', 0, 0)\n    return res'
          }
        ]
      },
      {
        id: 3,
        name: 'Valid Parenthesis String (with *)',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/valid-parenthesis-string/',
        flashcards: [
          {
            question: 'What does the * character represent in the Valid Parenthesis String problem?',
            answer: 'A wildcard that can be (, ), or empty.'
          },
          {
            question: 'What is the greedy approach to solve Valid Parenthesis String with *?',
            answer: 'Check from left to right and right to left, treating * as ( and ) respectively.'
          },
          {
            question: 'Write the Python code for the greedy solution.',
            answer: 'def checkValidString(s):\n    count_open = 0\n    for ch in s:\n        count_open += 1 if ch == \"(\" or ch == \"*\" else -1\n        if count_open < 0: return False\n    count_close = 0\n    for ch in reversed(s):\n        count_close += 1 if ch == \"\)\" or ch == \"*\" else -1\n        if count_close < 0: return False\n    return True'
          }
        ]
      },
      {
        id: 4,
        name: 'Check if a Parentheses String Can Be Valid (locked)',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/check-if-a-parentheses-string-can-be-valid/',
        flashcards: [
          {
            question: 'What does the locked string represent in the Check if a Parentheses String Can Be Valid problem?',
            answer: 'locked[i] = 1 means s[i] cannot be changed; locked[i] = 0 means it can.'
          },
          {
            question: 'What is the key insight for solving the locked parentheses problem?',
            answer: 'Use the same greedy two-pass approach as with *; treat unlocked positions as wildcards.'
          }
        ]
      },
      {
        id: 5,
        name: 'Minimum Swaps to Make the String Balanced',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-number-of-swaps-to-make-the-string-balanced/',
        flashcards: [
          {
            question: 'How do you compute the minimum number of swaps to balance a string of [ and ]?',
            answer: 'Count unmatched [ after removing all balanced pairs; answer is ceil(count/2).'
          }
        ]
      },
      {
        id: 6,
        name: 'Minimum Add/Remove to Make Parentheses Valid',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-add-to-make-parentheses-valid/',
        flashcards: [
          {
            question: 'How do you compute the minimum number of insertions to make a parentheses string valid?',
            answer: 'Count unmatched ( and ) as you iterate; sum gives the answer.'
          },
          {
            question: 'Write the Python code for minimum add to make valid.',
            answer: 'def minAddToMakeValid(s):\n    stack = 0\n    res = 0\n    for c in s:\n        if c == \"(\":\n            stack += 1\n        elif c == \"\)\":\n            if stack > 0: stack -= 1\n            else: res += 1\n    return res + stack'
          }
        ]
      },
      {
        id: 7,
        name: 'Minimum Remove to Make Valid Parentheses',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/minimum-remove-to-make-valid-parentheses/',
        flashcards: [
          {
            question: 'How do you compute the minimum number of removals to make a parentheses string valid?',
            answer: 'Track indices of unmatched ( and ) and remove them from the string.'
          },
          {
            question: 'Write the Python code for minimum remove to make valid.',
            answer: 'def minRemoveToMakeValid(s):\n    remove_indices = set()\n    stack_indices = []\n    for i,c in enumerate(s):\n        if c == \"(\":\n            stack_indices.append(i)\n        elif c == \"\)\":\n            if not stack_indices: remove_indices.add(i)\n            else: stack_indices.pop()\n    remove_indices |= set(stack_indices)\n    return \"\".join(c for i,c in enumerate(s) if i not in remove_indices)'
          }
        ]
      },
      {
        id: 8,
        name: 'Parenthesis in Calculators and Parsing',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/basic-calculator/',
        flashcards: [
          {
            question: 'How are stacks used in parsing expressions with parentheses (e.g., calculators)?',
            answer: 'Stacks help match parentheses and evaluate expressions inside them recursively.'
          },
          {
            question: 'What is the purpose of precomputing matching parentheses indices?',
            answer: 'To quickly find the closing parenthesis for any opening parenthesis during parsing.'
          }
        ]
      },
      {
        id: 9,
        name: 'Different Ways to Add Parentheses',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/different-ways-to-add-parentheses/',
        flashcards: [
          {
            question: 'What is the key insight for computing all possible results from adding parentheses in an expression?',
            answer: 'Divide and conquer with memoization (DP) is used to compute all possible results.'
          },
          {
            question: 'Write the Python code for different ways to add parentheses.',
            answer: 'from functools import cache\ndef diffWaysToCompute(expression):\n    @cache\n    def dp(s):\n        if not s: return []\n        if not any(c in [\'+\', \'-\', \'*\'] for c in s): return [int(s)]\n        return [eval(f\"{p}{s[i]}{q}\") for i in range(len(s)) for p in dp(s[:i]) for q in dp(s[i+1:]) if s[i] in [\'+\', \'-\', \'*\']]\n    return dp(expression)'
          }
        ]
      }
    ]
  },
  'trie': {
    title: 'Trie',
    problems: [
      {
        id: 1,
        name: 'Trie Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/trie/',
        flashcards: [
          { question: 'What is a trie?', answer: 'A tree-like data structure used to store strings, where each node represents a character.' },
          { question: 'What is the main advantage of a trie over a hash map for string storage?', answer: 'Tries allow efficient prefix queries and can store a large set of strings with shared prefixes compactly.' },
          { question: 'What is the root node in a trie?', answer: 'The starting node that does not represent any character.' }
        ]
      },
      {
        id: 2,
        name: 'Operations',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/implement-trie-prefix-tree/',
        flashcards: [
          { question: 'What are the main operations supported by a trie?', answer: 'Insert, search, and startsWith (prefix search).' },
          { question: 'How do you insert a word into a trie?', answer: 'Iterate through each character, creating nodes as needed, and mark the end of the word.' },
          { question: 'How do you check if a word exists in a trie?', answer: 'Traverse the trie following each character; if you reach the end and the node is marked as a word, it exists.' }
        ]
      },
      {
        id: 3,
        name: 'Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/implement-trie-prefix-tree/',
        flashcards: [
          { question: 'What is a real-world application of tries?', answer: 'Autocomplete, spellchecker, and prefix search.' }
        ]
      },
      {
        id: 4,
        name: 'Complexity',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/implement-trie-prefix-tree/',
        flashcards: [
          { question: 'What is the space complexity of a trie storing n words of average length L?', answer: 'O(n*L) in the worst case.' }
        ]
      }
    ]
  },
  'segment-tree': {
    title: 'Segment Tree',
    problems: [
      {
        id: 1,
        name: 'Segment Tree Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-mutable/',
        flashcards: [
          {
            question: 'What is the main advantage of a segment tree over prefix sums?',
            answer: 'Segment trees can handle both range queries and updates in O(log n) time, while prefix sums cannot handle updates efficiently.'
          },
          {
            question: 'What does each node in a segment tree represent?',
            answer: 'A segment (range) of the array, storing information about that segment.'
          },
          {
            question: 'What are the three main operations supported by a segment tree?',
            answer: 'Build (O(n)), Query (O(log n)), and Update (O(log n)).'
          },
          {
            question: 'What is the difference between a segment tree and a Fenwick tree (BIT)?',
            answer: 'Both have O(log n) query/update, but segment trees are more flexible and can support more types of range operations.'
          },
          {
            question: 'What are some common operations that can be implemented with a segment tree?',
            answer: 'Sum, Min, Max, XOR, Product, GCD, LCM, and more.'
          }
        ]
      },
      {
        id: 2,
        name: 'Segment Tree Template',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-mutable/',
        flashcards: [
          {
            question: 'What are the three cases to check in a segment tree query?',
            answer: '1. Segment is fully inside the query range, 2. Segment is fully outside, 3. Segment partially overlaps the query range.'
          },
          {
            question: 'What does the combine() function do in a segment tree?',
            answer: 'It merges the results of the left and right children to update the parent node.'
          },
          {
            question: 'What is lazy propagation in segment trees?',
            answer: 'A technique for efficient range updates, allowing you to update a range of values in O(log n) time.'
          },
          {
            question: 'Write the Python code for a sum segment tree node class.',
            answer: 'class Node:\n    def __init__(self, nums, lo, hi):\n        self.l = self.r = None\n        self.lo = lo\n        self.hi = hi\n        self.sum = nums[lo]\n        if lo < hi:\n            mid = (lo + hi) // 2\n            self.l = Node(nums, lo, mid)\n            self.r = Node(nums, mid+1, hi)\n            self.combine()\n    def combine(self):\n        self.sum = self.l.sum + self.r.sum\n    def update(self, i, x):\n        if i < self.lo or self.hi < i: return\n        if self.lo == self.hi: self.sum = x; return\n        self.l.update(i, x); self.r.update(i, x); self.combine()\n    def query(self, l, r):\n        if l <= self.lo <= self.hi <= r: return self.sum\n        if self.hi < l or r < self.lo: return 0\n        return self.l.query(l, r) + self.r.query(l, r)'
          }
        ]
      },
      {
        id: 3,
        name: 'Segment Tree Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/range-sum-query-mutable/',
        flashcards: [
          {
            question: 'How do you use a segment tree to solve Range Sum Query - Mutable?',
            answer: 'Build a sum segment tree, use update for point updates, and query for range sums.'
          },
          {
            question: 'How does coordinate compression help in segment tree problems?',
            answer: 'It maps large values to a smaller range, reducing memory usage and making the segment tree more efficient.'
          },
          {
            question: 'What is the key insight for using a segment tree in "Count of Smaller Numbers After Self"?',
            answer: 'Use a frequency segment tree with coordinate compression, scan right to left, and query for counts of smaller elements.'
          },
          {
            question: 'What is the main difference between a sum, min, and max segment tree?',
            answer: 'Only the combine() function and the neutral value for queries (0, inf, -inf) differ.'
          }
        ]
      },
      {
        id: 4,
        name: 'Segment Tree vs SortedList',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/count-of-smaller-numbers-after-self/',
        flashcards: [
          {
            question: 'When might you use a SortedList instead of a segment tree?',
            answer: 'When the API is simpler and the array is not too large.'
          },
          {
            question: 'What is the main similarity between segment trees and SortedList?',
            answer: 'Both can answer range queries and support updates, but with different APIs.'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced Segment Tree Concepts',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/132-pattern/',
        flashcards: [
          {
            question: 'What is lazy propagation and when is it necessary?',
            answer: 'A technique for efficient range updates, necessary when you need to update a range of values in O(log n) time.'
          },
          {
            question: 'What are the three key questions to ask when designing a segment tree?',
            answer: 'What operation, what do the indices represent, and what do the values represent?'
          },
          {
            question: 'How can segment trees be used to solve the 132 pattern problem?',
            answer: 'By using min/max segment trees and coordinate compression to efficiently check for the pattern.'
          }
        ]
      }
    ]
  },
  'monotonic-stack': {
    title: 'Monotonic Stack',
    problems: [
      {
        id: 1,
        name: 'Monotonic Stack Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/next-greater-element-i/',
        flashcards: [
          {
            question: 'What is a monotonic stack used for?',
            answer: 'Efficiently finding the next or previous greater/smaller element for each index in O(n) time.'
          },
          {
            question: 'How many variations of the monotonic stack template are there?',
            answer: '8: next/previous, greater/smaller, and strict/non-strict.'
          },
          {
            question: 'What are the three main variables you change in the monotonic stack template?',
            answer: 'X (init value), Y (iteration direction), Z (comparison operator).'
          },
          {
            question: 'What is the time and space complexity of the monotonic stack algorithm?',
            answer: 'O(n) time and O(n) space.'
          }
        ]
      },
      {
        id: 2,
        name: 'Monotonic Stack Template',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/next-greater-element-i/',
        flashcards: [
          {
            question: 'What is the master template for monotonic stack problems?',
            answer: 'A for loop with a stack and a comparison operator to determine when to pop.'
          },
          {
            question: 'How do you adapt the template for next/previous, greater/smaller, and strict/non-strict?',
            answer: 'Change the initial value, iteration direction, and comparison operator.'
          },
          {
            question: 'Write the Python code for the next greater element template.',
            answer: 'def ng(A):\n    n = len(A)\n    stack = []\n    res = [n] * n\n    for i in range(n):\n        while stack and A[stack[-1]] < A[i]:\n            res[stack[-1]] = i\n            stack.pop()\n        stack.append(i)\n    return res'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Monotonic Stack Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/daily-temperatures/',
        flashcards: [
          {
            question: 'How do you solve Daily Temperatures using a monotonic stack?',
            answer: 'Use a stack to keep indices of decreasing temperatures; when a warmer day is found, update the result.'
          },
          {
            question: 'How do you handle circular arrays in next greater element problems?',
            answer: 'Double the array and use modulo for indices.'
          },
          {
            question: 'What is the key insight for Largest Rectangle in Histogram?',
            answer: 'Use previous and next lower elements to determine the width for each bar.'
          },
          {
            question: 'Write the Python code for Largest Rectangle in Histogram using monotonic stacks.',
            answer: 'def largestRectangleArea(heights):\n    def nl(A):\n        n = len(A)\n        stack = []\n        res = [n] * n\n        for i in range(n):\n            while stack and A[stack[-1]] > A[i]:\n                res[stack[-1]] = i\n                stack.pop()\n            stack.append(i)\n        return res\n    def pl(A):\n        return [len(A)-1 - x for x in nl(A[::-1])[::-1]]\n    NL = nl(heights)\n    PL = pl(heights)\n    return max(heights[i] * (NL[i] - PL[i] - 1) for i in range(len(heights)))'
          }
        ]
      },
      {
        id: 4,
        name: 'Contribution Technique & Subarray Problems',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/sum-of-subarray-minimums/',
        flashcards: [
          {
            question: 'How do you use monotonic stacks to solve sum of subarray minimums?',
            answer: 'Find previous less and next less or equal for each element, then count subarrays where it is the minimum.'
          },
          {
            question: 'What is the contribution technique?',
            answer: 'Count how many subarrays each element is the min/max for, and sum their contributions.'
          },
          {
            question: 'Write the Python code for sum of subarray minimums using monotonic stacks.',
            answer: 'def sumSubarrayMins(A):\n    MOD = 10**9+7\n    n = len(A)\n    right = [n]*n\n    stack = []\n    for i in range(n):\n        while stack and A[stack[-1]] >= A[i]:\n            right[stack.pop()] = i\n        stack.append(i)\n    left = [-1]*n\n    stack = []\n    for i in range(n-1,-1,-1):\n        while stack and A[stack[-1]] > A[i]:\n            left[stack.pop()] = i\n        stack.append(i)\n    res = 0\n    for i in range(n):\n        l, r = left[i], right[i]\n        res = (res + A[i]*(i-l)*(r-i))%MOD\n    return res'
          }
        ]
      },
      {
        id: 5,
        name: 'Monotonic Deque & Sliding Window',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/sliding-window-maximum/',
        flashcards: [
          {
            question: 'How do you use a monotonic deque to solve the sliding window maximum problem?',
            answer: 'Maintain a deque of indices with decreasing values, pop from both ends as needed.'
          },
          {
            question: 'Why does the monotonic deque approach work for sliding window maximum?',
            answer: 'The deque always contains the indices of the largest elements in the window.'
          },
          {
            question: 'Write the Python code for sliding window maximum using a monotonic deque.',
            answer: 'from collections import deque\ndef maxSlidingWindow(nums, k):\n    n = len(nums)\n    dq = deque()\n    res = []\n    for i in range(n):\n        while dq and nums[dq[-1]] <= nums[i]:\n            dq.pop()\n        dq.append(i)\n        if i >= k-1:\n            res.append(nums[dq[0]])\n            if dq[0] == i-(k-1): dq.popleft()\n    return res'
          }
        ]
      }
    ]
  },
  'heap': {
    title: 'Heap',
    problems: [
      {
        id: 1,
        name: 'Heap Basics',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/kth-largest-element-in-an-array/',
        flashcards: [
          {
            question: 'What is a heap?',
            answer: 'A complete binary tree that allows O(log n) insertions, O(log n) pop from the top, and O(1) peek top.'
          },
          {
            question: 'What is the difference between a min heap and a max heap?',
            answer: 'Min heap stores the minimum at the top, max heap stores the maximum at the top.'
          },
          {
            question: 'How do you implement a max heap in Python using heapq?',
            answer: 'Negate all values before pushing to the heap.'
          },
          {
            question: 'What is the time complexity of heapify, heappush, and heappop?',
            answer: 'heapify is O(n), heappush and heappop are O(log n).'
          },
          {
            question: 'How does a heap compare to a SortedList?',
            answer: 'SortedList is more powerful (O(log n) insert, pop, search anywhere), but heap is simpler and more common.'
          }
        ]
      },
      {
        id: 2,
        name: 'Top K / Bottom K Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/kth-largest-element-in-an-array/',
        flashcards: [
          {
            question: 'What are three ways to solve top K problems?',
            answer: 'Sort (O(n log n)), heap of size K (O(n log K)), and quickselect (O(n) average).' 
          },
          {
            question: 'When do you use a min heap vs a max heap for top K/bottom K problems?',
            answer: 'Min heap for top K (remove smallest), max heap for bottom K (remove largest).' 
          },
          {
            question: 'What is the time complexity of finding the Kth largest element using a heap of size K?',
            answer: 'O(n log K).'
          },
          {
            question: 'Write the Python code for finding the Kth largest element using a heap.',
            answer: 'def findKthLargest(nums, k):\n    heap = []\n    for num in nums:\n        heappush(heap, num)\n        if len(heap) > k:\n            heappop(heap)\n    return heap[0]'
          }
        ]
      },
      {
        id: 3,
        name: 'Heap Applications',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/k-closest-points-to-origin/',
        flashcards: [
          {
            question: 'How do you use a heap to find the K closest points to the origin?',
            answer: 'Use a max heap of size K with negative squared distances.'
          },
          {
            question: 'How do you use a heap to solve the Top K Frequent Words problem?',
            answer: 'Use a heap of tuples (-frequency, word) and pop K times.'
          },
          {
            question: 'Write the Python code for K closest points to origin using a heap.',
            answer: 'def kClosest(points, k):\n    heap = []\n    for x, y in points:\n        heappush(heap, (-(x*x + y*y), x, y))\n        if len(heap) > k:\n            heappop(heap)\n    return [[x, y] for _, x, y in heap]'
          }
        ]
      },
      {
        id: 4,
        name: 'Median of Data Stream (2 Heaps)',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/problems/find-median-from-data-stream/',
        flashcards: [
          {
            question: 'How do you use two heaps to maintain the median of a data stream?',
            answer: 'Use a max heap for the left half and a min heap for the right half, balance sizes after each insertion.'
          },
          {
            question: 'What are the two invariants to maintain in the two-heap median approach?',
            answer: 'Size: left >= right and left <= right+1; Value: top of left <= top of right.'
          },
          {
            question: 'Write the Python code for MedianFinder using two heaps.',
            answer: 'class MedianFinder:\n    def __init__(self):\n        self.left = []\n        self.right = []\n    def addNum(self, num):\n        heappush(self.left, -num)\n        heappush(self.right, -heappop(self.left))\n        if len(self.left) < len(self.right):\n            heappush(self.left, -heappop(self.right))\n    def findMedian(self):\n        if len(self.left) > len(self.right):\n            return -self.left[0]\n        return (-self.left[0] + self.right[0]) / 2'
          }
        ]
      },
      {
        id: 5,
        name: 'Heap vs SortedList',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/find-median-from-data-stream/',
        flashcards: [
          {
            question: 'What is a key advantage of SortedList over heap?',
            answer: 'O(log n) deletion from anywhere, while heaps only allow O(log n) pop from the top.'
          },
          {
            question: 'When should you use a heap vs a SortedList?',
            answer: 'Heap for fast top access and streaming, SortedList for fast arbitrary deletion and order statistics.'
          }
        ]
      }
    ]
  },
  'two-pointer': {
    title: 'Two Pointers',
    problems: [
      {
        id: 1,
        name: 'Two Pointers Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/two-sum-ii-input-array-is-sorted/',
        flashcards: [
          {
            question: 'What is the main idea behind the two pointers technique?',
            answer: 'Use two indices to scan the array from both ends, discarding impossible pairs based on the problem condition.'
          },
          {
            question: 'What is the time and space complexity of the two pointers approach?',
            answer: 'O(n) time, O(1) space.'
          },
          {
            question: 'Why does the two pointers approach work for sorted arrays?',
            answer: 'Because we can discard large parts of the array based on the sorted order.'
          }
        ]
      },
      {
        id: 2,
        name: 'Two Pointers Template',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/two-sum-ii-input-array-is-sorted/',
        flashcards: [
          {
            question: 'What is the general template for the two pointers approach?',
            answer: 'Initialize i = 0, j = n-1, and move i or j based on the condition.'
          },
          {
            question: 'How do you generalize two pointers to triplets or higher?',
            answer: 'Fix one index and use two pointers on the rest.'
          },
          {
            question: 'Write the Python code for the two pointers template.',
            answer: 'def twoPointers(A):\n    i, j = 0, len(A)-1\n    while i < j:\n        if condition(A[i], A[j]):\n            j -= 1\n        else:\n            i += 1'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Two Pointers Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/3sum/',
        flashcards: [
          {
            question: 'How do you solve the Two Sum II problem using two pointers?',
            answer: 'Move i forward if the sum is too small, j backward if the sum is too large.'
          },
          {
            question: 'How do you solve the 3Sum problem using two pointers?',
            answer: 'Fix one index and use two pointers on the rest, skipping duplicates.'
          },
          {
            question: 'How do you solve the Valid Triangle Number problem using two pointers?',
            answer: 'Fix the largest side and use two pointers to count valid pairs.'
          },
          {
            question: 'Write the Python code for 3Sum using two pointers.',
            answer: 'def threeSum(nums):\n    nums.sort()\n    n = len(nums)\n    res = []\n    for i in range(n):\n        if i > 0 and nums[i] == nums[i-1]: continue\n        j, k = i+1, n-1\n        while j < k:\n            s = nums[i] + nums[j] + nums[k]\n            if s == 0:\n                res.append([nums[i], nums[j], nums[k]])\n                while j < k and nums[j] == nums[j+1]: j += 1\n                while j < k and nums[k] == nums[k-1]: k -= 1\n                j += 1; k -= 1\n            elif s < 0: j += 1\n            else: k -= 1\n    return res'
          }
        ]
      },
      {
        id: 4,
        name: 'Advanced Two Pointers Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/3sum-closest/',
        flashcards: [
          {
            question: 'How do you solve the 3Sum Closest problem using two pointers?',
            answer: 'Fix one index and use two pointers to find the closest sum.'
          },
          {
            question: 'How do you solve the Maximize Greatness problem using two pointers?',
            answer: 'Sort the array and use two pointers from the beginning to match values.'
          },
          {
            question: 'Write the Python code for Maximize Greatness using two pointers.',
            answer: 'def maximizeGreatness(nums):\n    nums.sort()\n    i, j, n = 0, 0, len(nums)\n    while j < n:\n        while j < n and nums[i] == nums[j]: j += 1\n        if j < n: i += 1; j += 1\n    return i'
          }
        ]
      }
    ]
  },
  'sliding-window': {
    title: 'Sliding Window',
    problems: [
      {
        id: 1,
        name: 'Basics & Intuition',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/sliding-window/',
        flashcards: [
          {
            question: 'What is the sliding window technique?',
            answer: 'A method to find optimal subarrays/substrings in O(n) time by maintaining a window with two pointers.'
          },
          {
            question: 'What is the brute force time complexity for checking all subarrays?',
            answer: 'O(n^2), since you check all {n choose 2} subarrays.'
          },
          {
            question: 'What is the key monotonic property required for sliding window?',
            answer: 'The is_valid(s[l,r]) function must be monotonic for all fixed l.'
          },
          {
            question: 'What are the two main sliding window templates?',
            answer: 'Max (longest/maximum) and Min (shortest/minimum) templates.'
          }
        ]
      },
      {
        id: 2,
        name: 'Max/Min Template',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/sliding-window/',
        flashcards: [
          {
            question: 'When do you update the result in the Max template?',
            answer: 'Outside the inner while loop, when the window is valid.'
          },
          {
            question: 'When do you update the result in the Min template?',
            answer: 'Inside the inner while loop, when the window is valid.'
          },
          {
            question: 'What is a common data structure for the window in sliding window problems?',
            answer: 'A frequency map (Counter) is commonly used.'
          },
          {
            question: 'What is the time complexity of most sliding window solutions?',
            answer: 'O(n), since each element is processed at most twice.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/longest-substring-without-repeating-characters/',
        flashcards: [
          {
            question: 'Describe the sliding window approach for Longest Substring Without Repeating Characters.',
            answer: 'Use a frequency map to track characters and move the left pointer when a repeat is found.'
          },
          {
            question: 'How do you solve Fruit Into Baskets (904) with sliding window?',
            answer: 'Track the count of each fruit type in the window and move the left pointer when more than 2 types.'
          },
          {
            question: 'How do you solve Max Consecutive Ones III (1004) with sliding window?',
            answer: 'Count the number of 0s in the window and move the left pointer when more than k.'
          },
          {
            question: 'How do you solve Minimum Size Subarray Sum (209) with sliding window?',
            answer: 'Expand the right pointer to increase sum, then move the left pointer to minimize window while sum >= target.'
          }
        ]
      },
      {
        id: 4,
        name: 'Fixed Window & Variants',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/sliding-window-maximum/',
        flashcards: [
          {
            question: 'What is the main difference between fixed and dynamic sliding window?',
            answer: 'Fixed window has constant size, dynamic window can grow or shrink.'
          },
          {
            question: 'How do you solve Sliding Window Maximum (239) with a monotonic deque?',
            answer: 'Maintain a deque of indices with decreasing values, pop from both ends as needed.'
          },
          {
            question: 'How do you count good substrings of size 3 with distinct characters?',
            answer: 'Use a frequency map and check window of size 3 for unique characters.'
          },
          {
            question: 'What is the time complexity of fixed window sliding window problems?',
            answer: 'O(n), since each window is processed in O(1).' 
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced & Variants',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/sliding-window/',
        flashcards: [
          {
            question: 'How do you solve problems that require "exactly K" using sliding window?',
            answer: 'Use the formula: at most K - at most (K-1).' 
          },
          {
            question: 'How can binary search be combined with sliding window?',
            answer: 'Binary search on window size, check validity with sliding window.'
          },
          {
            question: 'What is a 2D sliding window?',
            answer: 'A window that slides over a matrix/submatrix.'
          },
          {
            question: 'What is the main requirement for using sliding window in a problem?',
            answer: 'The is_valid function must be monotonic.'
          }
        ]
      }
    ]
  },
  'tree': {
    title: 'Trees',
    problems: [
      {
        id: 1,
        name: 'Basics & Structure',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        flashcards: [
          {
            question: 'What is a tree data structure?',
            answer: 'A hierarchical structure of nodes connected by edges, with a root and leaves.'
          },
          {
            question: 'What is the root of a tree?',
            answer: 'The topmost node in a tree.'
          },
          {
            question: 'What is a leaf node?',
            answer: 'A node with no children.'
          },
          {
            question: 'What is the difference between a binary tree and an n-ary tree?',
            answer: 'A binary tree has at most 2 children per node; an n-ary tree can have up to n.'
          }
        ]
      },
      {
        id: 2,
        name: 'Traversals',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        flashcards: [
          {
            question: 'What are the three main types of depth-first traversals?',
            answer: 'Preorder, Inorder, and Postorder.'
          },
          {
            question: 'What is the order of nodes visited in inorder traversal?',
            answer: 'Left, Root, Right.'
          },
          {
            question: 'Which traversal is used to get sorted values from a BST?',
            answer: 'Inorder traversal.'
          },
          {
            question: 'What is the base case for most tree recursion?',
            answer: 'If the node is null.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        flashcards: [
          {
            question: 'How do you invert a binary tree?',
            answer: 'Recursively swap left and right children for each node.'
          },
          {
            question: 'How do you check if two trees are the same?',
            answer: 'Recursively compare both value and structure.'
          },
          {
            question: 'How do you check if a tree is symmetric?',
            answer: 'Compare left and right subtrees as mirrors.'
          },
          {
            question: 'How do you compute the maximum depth of a binary tree?',
            answer: 'Recursively take 1 + max depth of left and right.'
          },
          {
            question: 'How do you check if a root-to-leaf path sums to a target?',
            answer: 'Recursively subtract node values from target and check at leaves.'
          }
        ]
      },
      {
        id: 4,
        name: 'BST Properties & Validation',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/problems/validate-binary-search-tree/',
        flashcards: [
          {
            question: 'What is the key property of a binary search tree (BST)?',
            answer: 'Left subtree < root < right subtree.'
          },
          {
            question: 'How do you validate a BST?',
            answer: 'Pass down min and max constraints during recursion.'
          },
          {
            question: 'What is the result of inorder traversal of a BST?',
            answer: 'A sorted array of values.'
          },
          {
            question: 'What is the time complexity of validating a BST?',
            answer: 'O(n), each node is visited once.'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced & State Tracking',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        flashcards: [
          {
            question: 'How do you find the lowest common ancestor (LCA) of two nodes in a binary tree?',
            answer: 'Recursively find the node where both nodes split.'
          },
          {
            question: 'How do you compute the sum of all root-to-leaf numbers?',
            answer: 'Track the path value as you traverse and sum at leaves.'
          },
          {
            question: 'What is the key pattern in tree problems involving state tracking?',
            answer: 'Combine results from subtrees and use global/local state.'
          },
          {
            question: 'What is the time complexity of most tree traversals?',
            answer: 'O(n), each node is visited once.'
          }
        ]
      }
    ]
  },
  'arrays-and-hashing': {
    title: 'Arrays and Hashing',
    problems: [
      {
        id: 1,
        name: 'Basics',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/array/',
        flashcards: [
          {
            question: 'What is the time complexity of adding and looking up an element in a set or dict in Python?',
            answer: 'O(1) on average.'
          },
          {
            question: 'What is the difference between a set and a dict?',
            answer: 'A set stores only unique keys, a dict stores key-value pairs.'
          },
          {
            question: 'What does defaultdict do that a regular dict does not?',
            answer: 'Returns a default value for missing keys.'
          },
          {
            question: 'What does Counter([1,2,3,1]) return?',
            answer: '{1: 2, 2: 1, 3: 1}'
          }
        ]
      },
      {
        id: 2,
        name: 'Hash Maps & Sets',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/hash-table/',
        flashcards: [
          {
            question: 'How do you solve the Two Sum problem efficiently?',
            answer: 'Use a hash map to store seen values and their indices.'
          },
          {
            question: 'What is the main advantage of using a set to check for duplicates?',
            answer: 'O(1) lookup for existence.'
          },
          {
            question: 'How does defaultdict(int) behave when accessing a missing key?',
            answer: 'Returns 0.'
          },
          {
            question: 'What is the output of len(set([1,2,2,3]))?',
            answer: '3.'
          }
        ]
      },
      {
        id: 3,
        name: 'Prefix Sums & Subarrays',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/prefix-sum/',
        flashcards: [
          {
            question: 'How do you count the number of subarrays with sum equal to k?',
            answer: 'Use a hash map to store prefix sums and their frequencies.'
          },
          {
            question: 'How do you check if a subarray sum is divisible by k?',
            answer: 'Track prefix sums modulo k in a hash map.'
          },
          {
            question: 'How do you count subarrays with an odd sum?',
            answer: 'Track even/odd prefix sums and their counts.'
          },
          {
            question: 'What is the time complexity of the optimal Subarray Sum Equals K solution?',
            answer: 'O(n), each element is processed once.'
          }
        ]
      },
      {
        id: 4,
        name: 'Anagrams',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/hash-table/',
        flashcards: [
          {
            question: 'How do you check if two strings are anagrams?',
            answer: 'Compare sorted strings or use Counter.'
          },
          {
            question: 'How do you group anagrams in a list of strings?',
            answer: 'Group by sorted string as key in a hash map.'
          },
          {
            question: 'What is an anagram?',
            answer: 'A word formed by rearranging the letters of another.'
          },
          {
            question: 'What is the time complexity of grouping anagrams?',
            answer: 'O(nm), n = number of strings, m = average string length.'
          }
        ]
      },
      {
        id: 5,
        name: 'Duplicates & Cycles',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/hash-table/',
        flashcards: [
          {
            question: 'How do you check if an array contains duplicates?',
            answer: 'Compare length of set and array.'
          },
          {
            question: 'How do you check for nearby duplicates within k distance?',
            answer: 'Use a hash map to track last seen indices.'
          },
          {
            question: 'How do you find the minimum number of swaps to sort an array by digit sum?',
            answer: 'Decompose into cycles and sum (cycle length - 1).'
          },
          {
            question: 'What is the main use of sets in graph traversals?',
            answer: 'Track visited nodes.'
          }
        ]
      }
    ]
  },
  'graph': {
    title: 'Graphs',
    problems: [
      {
        id: 1,
        name: 'Basics & Types',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/graph/',
        flashcards: [
          {
            question: 'What is a graph?',
            answer: 'A set of vertices (nodes) and edges (connections) between them.'
          },
          {
            question: 'What is the difference between a directed and undirected graph?',
            answer: 'Directed edges have a direction; undirected edges do not.'
          },
          {
            question: 'What is a weighted graph?',
            answer: 'A graph where edges have associated values (weights).'
          },
          {
            question: 'What is an adjacency list?',
            answer: 'A mapping from each node to its neighbors.'
          }
        ]
      },
      {
        id: 2,
        name: 'Traversals (BFS/DFS)',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/breadth-first-search/',
        flashcards: [
          {
            question: 'What does BFS stand for?',
            answer: 'Breadth First Search.'
          },
          {
            question: 'What is the main property of BFS in unweighted graphs?',
            answer: 'Finds shortest path from source to all nodes.'
          },
          {
            question: 'What is the main property of DFS?',
            answer: 'Recursively explores as far as possible along each branch.'
          },
          {
            question: 'What is the time complexity of BFS/DFS on a graph with n nodes and m edges?',
            answer: 'O(n + m), each node and edge is visited at most once.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/graph/',
        flashcards: [
          {
            question: 'How do you count the number of islands in a grid?',
            answer: 'Use BFS or DFS to mark all connected land cells.'
          },
          {
            question: 'How do you clone a graph?',
            answer: 'Use BFS/DFS and a map from old to new nodes.'
          },
          {
            question: 'How do you find the number of connected components in an undirected graph?',
            answer: 'Run DFS/BFS from each unvisited node.'
          },
          {
            question: 'How do you check if all rooms can be visited given keys in each room?',
            answer: 'DFS from room 0, check if all rooms are visited.'
          }
        ]
      },
      {
        id: 4,
        name: 'Dijkstra & Shortest Path',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/shortest-path/',
        flashcards: [
          {
            question: 'What is Dijkstra algorithm used for?',
            answer: 'Finding shortest paths in non-negative weighted graphs.'
          },
          {
            question: 'What data structure is used in Dijkstra algorithm?',
            answer: 'Min heap (priority queue).'
          },
          {
            question: 'What is the time complexity of Dijkstra algorithm with a binary heap?',
            answer: 'O((n + m) log n), where n = nodes, m = edges.'
          },
          {
            question: 'What is 0-1 BFS?',
            answer: 'A BFS optimization for graphs with edge weights 0 or 1, using a deque.'
          }
        ]
      },
      {
        id: 5,
        name: 'Topological Sort & Advanced',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/topological-sort/',
        flashcards: [
          {
            question: 'What is topological sort used for?',
            answer: 'Ordering nodes in a DAG so all edges go forward.'
          },
          {
            question: 'What is Kahn algorithm?',
            answer: 'A BFS-based algorithm for topological sort.'
          },
          {
            question: 'How do you detect a cycle in a directed graph?',
            answer: 'If topological sort is not possible (not all nodes processed), there is a cycle.'
          },
          {
            question: 'What is a strongly connected component (SCC)?',
            answer: 'A maximal set of nodes where each node is reachable from every other.'
          }
        ]
      }
    ]
  },
  'dynamic-programming': {
    title: 'Dynamic Programming',
    problems: [
      {
        id: 1,
        name: 'Basics & Intuition',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        flashcards: [
          {
            question: 'What is dynamic programming (DP)?',
            answer: 'A technique for solving problems by breaking them into overlapping subproblems and storing results to avoid recomputation.'
          },
          {
            question: 'What is the difference between recursion and DP?',
            answer: 'DP stores results of subproblems (memoization or tabulation), recursion does not.'
          },
          {
            question: 'What is the time complexity of naive recursive Fibonacci?',
            answer: 'O(2^n), exponential.'
          },
          {
            question: 'What is memoization?',
            answer: 'Caching results of subproblems to avoid recomputation.'
          }
        ]
      },
      {
        id: 2,
        name: 'Recurrence & Memoization',
        difficulty: 'Easy',
        leetcodeUrl: 'https://leetcode.com/problems/fibonacci-number/',
        flashcards: [
          {
            question: 'What is the recurrence relation for Fibonacci numbers?',
            answer: 'F(n) = F(n-1) + F(n-2) with base cases F(0)=0, F(1)=1.'
          },
          {
            question: 'How does memoization improve recursive solutions?',
            answer: 'It avoids recomputation by storing results, reducing time complexity.'
          },
          {
            question: 'Which Python decorator is commonly used for memoization?',
            answer: '@lru_cache.'
          }
        ]
      },
      {
        id: 3,
        name: 'Classic Problems',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        flashcards: [
          {
            question: 'What is the Climbing Stairs problem?',
            answer: 'Given n steps, find the number of ways to climb to the top taking 1 or 2 steps at a time.'
          },
          {
            question: 'What is the House Robber problem?',
            answer: 'Find the maximum sum of non-adjacent elements in an array.'
          },
          {
            question: 'What is the Partition Equal Subset Sum problem?',
            answer: 'Determine if an array can be partitioned into two subsets with equal sum.'
          },
          {
            question: 'What is the O(n^2) DP solution for Longest Increasing Subsequence (LIS)?',
            answer: 'dp[i] = max(1, 1 + dp[j] for all j < i and nums[j] < nums[i]).'
          }
        ]
      },
      {
        id: 4,
        name: 'State & Dimensions',
        difficulty: 'Medium',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        flashcards: [
          {
            question: 'What is a "state" in DP?',
            answer: 'A unique configuration of parameters that define a subproblem.'
          },
          {
            question: 'What is the time complexity of a DP with state dp(i, j) where both i and j go from 0 to n?',
            answer: 'O(n^2), since there are n^2 possible states.'
          },
          {
            question: 'Give an example of a 2D DP problem.',
            answer: 'Edit Distance, where dp[i][j] represents the min edits to convert s[0:i] to t[0:j].'
          }
        ]
      },
      {
        id: 5,
        name: 'Advanced & Variants',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        flashcards: [
          {
            question: 'What is bitmask DP?',
            answer: 'A DP technique that uses bits to efficiently represent subsets or states.'
          },
          {
            question: 'What is digit DP?',
            answer: 'A DP technique for counting or optimizing over numbers digit by digit, often with constraints.'
          },
          {
            question: 'What is tree DP?',
            answer: 'DP on trees, where subproblems are defined on subtrees.'
          },
          {
            question: 'What is the main challenge in digit DP problems?',
            answer: 'Handling constraints digit by digit and managing state transitions.'
          }
        ]
      },
      {
        id: 6,
        name: 'Interval, Greedy, and Binary Search DP',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/dynamic-programming/',
        flashcards: [
          {
            question: 'What is interval DP?',
            answer: 'A DP technique where subproblems are defined over intervals (i, j), e.g., Matrix Chain Multiplication.'
          },
          {
            question: 'How is binary search used with DP?',
            answer: 'To optimize the search for a valid DP state or answer, such as in LIS O(n log n) or min/max value problems.'
          },
          {
            question: 'What is a classic greedy + DP problem?',
            answer: 'Weighted Interval Scheduling (Job Scheduling), solved with DP and greedy sorting.'
          },
          {
            question: 'What is the DP state for Maximum Profit in Job Scheduling?',
            answer: 'dp(i): max profit for jobs[i..n-1], solved by sorting and binary search.'
          }
        ]
      },
      {
        id: 7,
        name: 'Tree DP',
        difficulty: 'Hard',
        leetcodeUrl: 'https://leetcode.com/tag/tree/',
        flashcards: [
          {
            question: 'What is tree DP?',
            answer: 'DP where subproblems are defined on subtrees of a tree.'
          },
          {
            question: 'How do you avoid cycles in tree DP?',
            answer: 'By passing the parent node as a parameter and skipping it in recursion.'
          },
          {
            question: 'What is a classic tree DP problem?',
            answer: 'House Robber III (LeetCode 337).'
          },
          {
            question: 'What is the typical DP state for tree DP?',
            answer: 'dp(node): optimal value for the subtree rooted at node.'
          }
        ]
      }
    ]
  },
  // Add more topics...
};



function Flashcards() {
  const [completedSections, setCompletedSections] = useState(() => {
    const saved = localStorage.getItem('completedFlashcardSections');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('completedFlashcardSections', JSON.stringify(completedSections));
  }, [completedSections]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calculate progress statistics
  const progressStats = React.useMemo(() => {
    const allProblems = Object.entries(flashcardsByTopic).flatMap(([topicId, topic]) => 
      topic.problems.map(problem => ({
        ...problem,
        topicId // Add topicId to each problem
      }))
    );
    const total = allProblems.length;
    const completed = Object.values(completedSections).filter(Boolean).length;
    
    // By difficulty
    const difficultyStats = allProblems.reduce((acc, problem) => {
      if (!acc[problem.difficulty]) {
        acc[problem.difficulty] = {
          total: 0,
          completed: 0
        };
      }
      acc[problem.difficulty].total++;
      if (completedSections[`${problem.topicId}-${problem.id}`]) {
        acc[problem.difficulty].completed++;
      }
      return acc;
    }, {});

    // By topic
    const topicStats = Object.entries(flashcardsByTopic).reduce((acc, [topicId, topic]) => {
      acc[topicId] = {
        title: topic.title,
        total: topic.problems.length,
        completed: topic.problems.filter(problem => 
          completedSections[`${topicId}-${problem.id}`]
        ).length
      };
      return acc;
    }, {});

    return {
      total,
      completed,
      difficultyStats,
      topicStats
    };
  }, [completedSections]);

  const handleSectionToggle = (topicId, problemId) => {
    const key = `${topicId}-${problemId}`;
    setCompletedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [previousTopic, setPreviousTopic] = useState(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [currentDeck, setCurrentDeck] = useState(null);
  const [isStudyMode, setIsStudyMode] = useState(false);
  const [topicProgressOpen, setTopicProgressOpen] = useState(false);
  const [cardResults, setCardResults] = useState([]); // null = unmarked, true = correct, false = incorrect
  const [showCompleteMenu, setShowCompleteMenu] = useState(false);

  const handleTopicSelect = (topicId) => {
    setSelectedTopic(topicId);
    setIsStudyMode(false);
  };

  const handleStartFlashcards = (cards, problemName, topic) => {
    setCurrentDeck({
      cards: cards.map(card => ({
        ...card,
        question: card.question || '',
        answer: card.answer || '',
        problemName,
        topic
      }))
    });
    setCurrentCardIndex(0);
    setIsStudyMode(true);
    setCardResults(Array(cards.length).fill(null));
    setShowCompleteMenu(false);
  };

  const createRandomDeck = () => {
    setPreviousTopic(selectedTopic);
    // Get all flashcards from all topics and problems
    const allCards = Object.values(flashcardsByTopic).flatMap(topic => 
      topic.problems.flatMap(problem => 
        problem.flashcards.map(card => ({
          ...card,
          topic: topic.title,
          problemName: problem.name
        }))
      )
    );
    
    // Randomly select 5 cards
    const randomCards = [];
    const cardsCopy = [...allCards];
    for (let i = 0; i < 5 && cardsCopy.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * cardsCopy.length);
      randomCards.push(cardsCopy.splice(randomIndex, 1)[0]);
    }

    setCurrentDeck({
      cards: randomCards
    });
    setSelectedTopic('random');
    setCurrentCardIndex(0);
    setIsStudyMode(true);
  };

  const handleNextCard = () => {
    if (currentCardIndex + 1 < currentDeck.cards.length) {
      setCurrentCardIndex(currentCardIndex + 1);
    } else {
      setCurrentCardIndex(0); // Loop back to first card
    }
  };

  const handleStudyAll = (topicId) => {
    // Get all flashcards from all problems in the topic
    const allCards = flashcardsByTopic[topicId].problems.flatMap(problem => 
      problem.flashcards.map(card => ({
        ...card,
        problemName: problem.name,
        topic: flashcardsByTopic[topicId].title
      }))
    );

    setCurrentDeck({
      cards: allCards
    });
    setSelectedTopic(topicId);
    setCurrentCardIndex(0);
    setIsStudyMode(true);
  };

  // Find topic/problem for current deck (for marking as completed)
  let currentProblem = null, currentTopicId = null;
  if (selectedTopic && selectedTopic !== 'random' && currentDeck && currentDeck.cards.length > 0) {
    const topicObj = flashcardsByTopic[selectedTopic];
    if (topicObj) {
      currentProblem = topicObj.problems.find(
        p => p.name === currentDeck.cards[0].problemName
      );
      currentTopicId = selectedTopic;
    }
  }
  const completeKey = currentProblem ? `${currentTopicId}-${currentProblem.id}` : null;
  const allMarked = cardResults.length > 0 && cardResults.every(r => r !== null);
  const numCorrect = cardResults.filter(r => r === true).length;
  const isStudyAll = currentDeck && currentDeck.cards.length > 0 && currentDeck.cards[0].problemName && currentDeck.cards[0].topic && (currentDeck.cards[0].problemName.includes('All Cards') || currentDeck.cards[0].topic.includes('All Cards'));

  const { user } = useAuth();
  const { hasActiveSubscription, loading: subLoading } = useSubscriptionStatus();

  return (
    <Container maxWidth="xl" sx={{
      py: { xs: 5, md: 7 },
      minHeight: 'calc(100vh - 120px)',
      '& .MuiPaper-root, & .MuiCard-root': {
        background: (theme) => `${theme.palette.background.paper} !important`,
        backgroundImage: 'none !important',
        border: (theme) => `1px solid ${theme.palette.divider} !important`,
        boxShadow: (theme) => theme.palette.mode === 'dark'
          ? '0 24px 80px rgba(0,0,0,0.24) !important'
          : '0 24px 80px rgba(82,107,170,0.12) !important',
        backdropFilter: 'blur(18px)',
      },
      '& .MuiButton-contained': {
        background: (theme) => `${theme.palette.primary.main} !important`,
        color: (theme) => `${theme.palette.primary.contrastText} !important`,
      },
    }}>
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 5 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 0.6,
            mb: 2,
            borderRadius: 999,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 800,
          }}
        >
          Flashcards
        </Typography>
        <Typography variant="h2" sx={{ color: 'text.primary', fontSize: { xs: '2.8rem', md: '4.8rem' }, lineHeight: 0.95, fontWeight: 850 }}>
          Review until it sticks.
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7, mt: 2 }}>
          Use active recall to keep templates, tradeoffs, and problem signals ready for interviews.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Box sx={{ mb: 3 }}>
            <Tooltip
              title={
                subLoading
                  ? 'Checking subscription...'
                  : !user
                  ? 'Sign in to access Random Cards (Premium)'
                  : !hasActiveSubscription
                  ? 'Random Cards is for Premium users only'
                  : ''
              }
              disableHoverListener={user && hasActiveSubscription}
              disableFocusListener={user && hasActiveSubscription}
              disableTouchListener={user && hasActiveSubscription}
            >
              <span>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ShuffleIcon />}
              onClick={createRandomDeck}
              sx={{
                p: 2,
              }}
                  disabled={subLoading || !user || !hasActiveSubscription}
            >
              Random Cards (5)
            </Button>
              </span>
            </Tooltip>
          </Box>
          <Sidebar 
            mode="flashcards" 
            onTopicSelect={handleTopicSelect}
            selectedTopic={selectedTopic}
            completedChapters={completedSections}
            flashcardsByTopic={flashcardsByTopic}
          />
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          {isStudyMode ? (
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsStudyMode(false);
                    setCurrentDeck(null);
                    if (selectedTopic === 'random' && previousTopic) {
                      setSelectedTopic(previousTopic);
                      setPreviousTopic(null);
                    }
                  }}
                >
                  Back
                </Button>
                {currentDeck?.cards[currentCardIndex] && (
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: 'secondary.main',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    From: {currentDeck.cards[currentCardIndex].topic} → {currentDeck.cards[currentCardIndex].problemName}
                </Typography>
              )}
              </Box>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  textAlign: 'center',
                  color: 'text.primary',
                  fontWeight: 850,
                  mb: 4
                }}
              >
                {currentDeck?.cards[currentCardIndex]?.problemName} Flashcards
              </Typography>
              <Box sx={{ mb: 4 }}>
                <LinearProgress
                  variant="determinate"
                  value={currentDeck ? ((currentCardIndex + 1) / currentDeck.cards.length) * 100 : 0}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: 'secondary.main'
                    }
                  }}
                />
              </Box>
              {/* Card Navigation */}
              <Box sx={{
                display: 'block',
                maxWidth: '100%',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                mb: 2,
                px: 1,
                scrollbarColor: 'auto',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#0a0d14',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
              }}>
                {currentDeck.cards.map((c, idx) => (
                  <Button
                    key={idx}
                    size="small"
                    variant={idx === currentCardIndex ? 'contained' : 'outlined'}
                    color={idx === currentCardIndex ? 'primary' : 'inherit'}
                    onClick={() => setCurrentCardIndex(idx)}
                    sx={{ 
                      minWidth: 36, 
                      borderRadius: '50%', 
                      fontWeight: idx === currentCardIndex ? 700 : 400, 
                      display: 'inline-block', 
                      mx: 0.5,
                      bgcolor:
                        cardResults[idx] === true
                          ? 'rgba(76, 175, 80, 0.5)'
                          : cardResults[idx] === false
                          ? 'rgba(244, 67, 54, 0.5)'
                          : undefined,
                      borderColor:
                        cardResults[idx] === true
                          ? '#15905f'
                          : cardResults[idx] === false
                          ? '#c24150'
                          : undefined,
                      color:
                        cardResults[idx] === true
                          ? 'text.primary'
                          : cardResults[idx] === false
                          ? 'text.primary'
                          : undefined,
                    }}
                  >
                    {idx + 1}
                  </Button>
                ))}
              </Box>
              <FlashcardView 
                card={currentDeck.cards[currentCardIndex]}
                onNext={handleNextCard}
                total={currentDeck.cards.length}
                current={currentCardIndex}
                markStatus={cardResults[currentCardIndex]}
                onMarkCorrect={() => {
                  setCardResults(results => {
                    const updated = [...results];
                    updated[currentCardIndex] = true;
                    return updated;
                  });
                }}
                onMarkIncorrect={() => {
                  setCardResults(results => {
                    const updated = [...results];
                    updated[currentCardIndex] = false;
                    return updated;
                  });
                }}
                allMarked={allMarked}
              />
              {/* Complete Section button */}
              {allMarked && (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  sx={{ mt: 4, fontWeight: 700, fontSize: '1.1rem' }}
                  onClick={() => setShowCompleteMenu(true)}
                >
                  Complete Section
                </Button>
              )}
              {/* Completion Modal/Menu */}
              <Dialog open={showCompleteMenu} onClose={() => setShowCompleteMenu(false)}>
                <DialogTitle>Section Completed</DialogTitle>
                <DialogContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Your score: {numCorrect} / {cardResults.length}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setCardResults(Array(currentDeck.cards.length).fill(null));
                      setCurrentCardIndex(0);
                      setShowCompleteMenu(false);
                    }}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsStudyMode(false);
                      setCurrentDeck(null);
                      setShowCompleteMenu(false);
                    }}
                  >
                    Back
                  </Button>
                  {/* Mark as completed if 100% correct, not random/study all, and not already completed */}
                  {numCorrect === cardResults.length &&
                    currentProblem &&
                    currentTopicId &&
                    !isStudyAll &&
                    !completedSections[completeKey] && (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => {
                          setCompletedSections(prev => ({
                            ...prev,
                            [completeKey]: true
                          }));
                          setShowCompleteMenu(false);
                          setIsStudyMode(false);
                          setCurrentDeck(null);
                        }}
                      >
                        Mark Section as Completed
                      </Button>
                  )}
                </DialogActions>
              </Dialog>
            </Container>
          ) : (
            <>
              {/* Progress Section */}
              <Paper elevation={0} sx={{ p: 3, mb: 4 }}>
                {/* Overall Progress */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary' }}>
                      Total Progress
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'secondary.main' }}>
                      {progressStats.completed} / {progressStats.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(progressStats.completed / progressStats.total) * 100}
                    sx={{ 
                      height: 10, 
                      borderRadius: 999,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'secondary.main',
                        borderRadius: 999,
                      }
                    }} 
                  />
                </Box>

                {/* Difficulty Progress */}
                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                  {Object.entries(progressStats.difficultyStats).map(([difficulty, stats]) => (
                    <Box key={difficulty} sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ 
                          color: difficulty === 'Easy' ? '#15905f' : 
                                 difficulty === 'Medium' ? '#b7791f' : '#c24150'
                        }}>
                          {difficulty}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                          {stats.completed} / {stats.total}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={(stats.completed / stats.total) * 100}
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          backgroundColor: difficulty === 'Easy' ? 'rgba(0, 255, 0, 0.2)' : 
                                          difficulty === 'Medium' ? 'rgba(255, 153, 0, 0.2)' : 
                                          'rgba(255, 0, 0, 0.2)',
                          border: `1px solid ${
                            difficulty === 'Easy' ? 'rgba(0, 255, 0, 0.3)' : 
                            difficulty === 'Medium' ? 'rgba(255, 153, 0, 0.3)' : 
                            'rgba(255, 0, 0, 0.3)'
                          }`,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: difficulty === 'Easy' ? '#15905f' : 
                                            difficulty === 'Medium' ? '#b7791f' : '#c24150'
                          }
                        }} 
                      />
                    </Box>
                  ))}
                </Box>

                {/* Topic Progress */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 0 }}>
                    Topic Progress
                  </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setTopicProgressOpen((open) => !open)}
                      sx={{ ml: 1, color: 'text.primary' }}
                      aria-label={topicProgressOpen ? 'Collapse' : 'Expand'}
                    >
                      {topicProgressOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={topicProgressOpen}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {Object.entries(progressStats.topicStats)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([topicId, stats]) => (
                        <Box key={topicId} sx={{ width: 'calc(33.33% - 16px)', minWidth: 250 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                              {stats.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'secondary.main', ml: 1 }}>
                              {stats.completed} / {stats.total}
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={(stats.completed / stats.total) * 100}
                            sx={{ 
                         height: 4, 
                              borderRadius: 999,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: 'secondary.main',
                                borderRadius: 999,
                              }
                            }} 
                          />
                        </Box>
                    ))}
                  </Box>
                  </Collapse>
                </Box>
              </Paper>


              {!selectedTopic && (
            <Paper 
              sx={{ 
                p: 4,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5" sx={{ color: 'text.primary' }}>
                Select a topic from the sidebar to view flashcards
              </Typography>
            </Paper>
          )}

              {!isStudyMode && selectedTopic && flashcardsByTopic[selectedTopic] && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleStudyAll(selectedTopic)}
                    sx={{
                      p: 2,
                    }}
                  >
                    Study All {flashcardsByTopic[selectedTopic]?.title} Cards
                  </Button>
                </Box>
              )}
              {selectedTopic && (
              <Paper 
                sx={{ 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}></TableCell>
                        <TableCell>Problem</TableCell>
                        <TableCell>LeetCode</TableCell>
                        <TableCell>Difficulty</TableCell>
                        <TableCell>Cards</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedTopic && flashcardsByTopic[selectedTopic]?.problems.map((problem) => (
                        <ProblemRow 
                          key={problem.id} 
                          problem={problem}
                          topicId={selectedTopic}
                          isCompleted={completedSections[`${selectedTopic}-${problem.id}`]}
                          onToggle={() => handleSectionToggle(selectedTopic, problem.id)}
                          onStartFlashcards={(cards) => handleStartFlashcards(cards, problem.name, flashcardsByTopic[selectedTopic]?.title)}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

function FlashcardView({ card, onNext, total, current, markStatus, onMarkCorrect, onMarkIncorrect, allMarked }) {
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    setIsFlipped(false);
  }, [card]);

  if (!card) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          No card data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: 400 }}>
      {/* Front of card */}
      <Card
        onClick={() => setIsFlipped(!isFlipped)}
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          display: isFlipped ? 'none' : 'block',
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ color: 'text.primary', textAlign: 'center' }}>
            {card.question}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Click to flip
          </Typography>
        </CardContent>
      </Card>

      {/* Back of card */}
      <Card
        onClick={() => setIsFlipped(!isFlipped)}
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          display: isFlipped ? 'block' : 'none',
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ color: 'secondary.main', mb: 2, textAlign: 'center' }}>
            Answer
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', textAlign: 'center', whiteSpace: 'pre-line' }}>
            {card.answer || 'No answer available'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Click to flip back
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ position: 'absolute', bottom: -50, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ color: 'text.secondary' }}>
          Card {current + 1} of {total}
        </Typography>
        {!allMarked && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={markStatus === true ? 'contained' : 'outlined'}
              color="success"
              onClick={onMarkCorrect}
              sx={{ minWidth: 120 }}
            >
              Mark Correct
            </Button>
            <Button
              variant={markStatus === false ? 'contained' : 'outlined'}
              color="error"
              onClick={onMarkIncorrect}
              sx={{ minWidth: 120 }}
            >
              Mark Incorrect
            </Button>
        <Button
          variant="contained"
          onClick={() => {
            setIsFlipped(false);
            onNext();
          }}
          sx={{
            background: '#0a0d14',
            '&:hover': {
              background: '#05070c',
            }
          }}
        >
          Next Card
        </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function ProblemRow({ problem, onStartFlashcards, topicId, isCompleted, onToggle }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow 
        sx={{ 
          '&:hover': { 
            bgcolor: 'action.hover'
          },
          cursor: 'pointer'
        }}
      >
        <TableCell>
          <Checkbox
            checked={isCompleted}
            onChange={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            sx={{
              color: 'secondary.main',
              '&.Mui-checked': {
                color: 'secondary.main',
              },
            }}
          />
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={() => setOpen(!open)}>
          {problem.name}
        </TableCell>
        <TableCell>
          <Link
            component={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? RouterLink : 'a'}
            to={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name) : undefined}
            href={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? undefined : internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)}
            target={isInternalRoute(internalProblemPathFromLeetcodeUrl(problem.leetcodeUrl, problem.name)) ? undefined : '_blank'}
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'secondary.main'
            }}
          >
            Solve <LaunchIcon fontSize="small" />
          </Link>
        </TableCell>
        <TableCell>
          <Typography
            sx={{
              color: problem.difficulty === 'Easy' ? '#15905f' :
                     problem.difficulty === 'Medium' ? '#b7791f' : '#c24150'
            }}
          >
            {problem.difficulty}
          </Typography>
        </TableCell>
        <TableCell>{problem.flashcards.length} cards</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Button
                variant="contained"
                onClick={() => onStartFlashcards(problem.flashcards)}
                sx={{
                  mb: 2,
                  background: '#0a0d14',
                  '&:hover': {
                    background: '#05070c',
                  }
                }}
              >
                Start Flashcards ({problem.flashcards.length} cards)
              </Button>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                Preview:
              </Typography>
              {problem.flashcards.map((card, index) => (
                <Paper 
                  key={index}
                  sx={{ 
                    p: 2,
                    mb: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography sx={{ color: 'text.primary', mb: 1 }}>
                    Q: {card.question}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    A: Click to reveal...
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default Flashcards; 
